import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

import { useChecklistState } from "common/components/QuickStartChecklist";

const PADDING = 6;
const SPOTLIGHT_RADIUS = 48;
const Z_INDEX = 1090;
const TARGET_RETRY_INTERVAL_MS = 100;
const TARGET_MAX_WAIT_MS = 1500;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(63, 139, 231, 0.5); }
  60% { box-shadow: 0 0 4px 24px rgba(63, 139, 231, 0); }
  100% { box-shadow: 0 0 0 24px rgba(63, 139, 231, 0); }
`;

const Outline = styled.div`
  position: fixed;
  pointer-events: none;
  border: 2px solid ${({ theme }) => theme.colors.b50};
  z-index: ${Z_INDEX + 1};
  animation: ${pulse} 1.6s ease-out infinite;
  /* Only ease size — never position. Easing top/left made the outline lag
     behind the target on scroll and drift from the SVG cutout (DSH-856). */
  transition: width 0.15s ease, height 0.15s ease;
`;

const OverlaySvg = styled.svg`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: ${Z_INDEX};
  pointer-events: none;
`;

const readBorderRadius = (el: HTMLElement): number => {
  const value = parseFloat(getComputedStyle(el).borderTopLeftRadius);
  return Number.isFinite(value) && value > 0 ? value : 0;
};

/**
 * Build a single SVG path that fills the viewport but has a rounded-rectangle
 * hole at `cutout`. Using even-odd fill rule + visiblePainted hit-testing,
 * the hole area is genuinely unpainted, so clicks pass through to elements
 * below — unlike a <mask>-based approach where the rect's bounding box is
 * considered painted regardless of the mask.
 */
const buildOverlayPath = (
  cutout: { x: number; y: number; width: number; height: number },
  radius: number,
  vw: number,
  vh: number
): string => {
  const r = radius;
  const { x, y, width: w, height: h } = cutout;
  const outer = `M 0,0 H ${vw} V ${vh} H 0 Z`;
  const inner =
    `M ${x + r},${y} ` +
    `H ${x + w - r} ` +
    `A ${r},${r} 0 0 1 ${x + w},${y + r} ` +
    `V ${y + h - r} ` +
    `A ${r},${r} 0 0 1 ${x + w - r},${y + h} ` +
    `H ${x + r} ` +
    `A ${r},${r} 0 0 1 ${x},${y + h - r} ` +
    `V ${y + r} ` +
    `A ${r},${r} 0 0 1 ${x + r},${y} Z`;
  return `${outer} ${inner}`;
};

const GuideHighlight = () => {
  const { activeGuide, endGuide } = useChecklistState();
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [radius, setRadius] = useState<number>(SPOTLIGHT_RADIUS);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 0,
    h: typeof window !== "undefined" ? window.innerHeight : 0,
  }));

  useEffect(() => {
    if (!activeGuide) {
      setRect(null);
      return;
    }

    const selector = activeGuide.selector;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let target: HTMLElement | null = null;
    const start = Date.now();

    const update = () => {
      if (!target || cancelled) return;
      setRect(target.getBoundingClientRect());
      const targetRadius = readBorderRadius(target);
      setRadius(targetRadius > 0 ? targetRadius + PADDING : SPOTLIGHT_RADIUS);
    };

    const onTargetClick = () => endGuide();

    const attach = (el: HTMLElement) => {
      target = el;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      update();
      setTimeout(update, 350);
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(el);
      el.addEventListener("mousedown", onTargetClick);
    };

    const tryFind = () => {
      if (cancelled) return;
      const candidates = document.querySelectorAll<HTMLElement>(selector);
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Collect all candidates whose center is inside the viewport. Using the
      // center (not just rect overlap) skips off-screen carousel duplicates
      // and partially-clipped cards at the carousel's left/right edges.
      const visible: HTMLElement[] = [];
      for (const el of Array.from(candidates)) {
        const r = el.getBoundingClientRect();
        if (r.width <= 0 || r.height <= 0) continue;
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        if (cx >= 0 && cx <= vw && cy >= 0 && cy <= vh) visible.push(el);
      }
      if (visible.length > 0) {
        // Prefer the configured skipIndex (e.g., second visible card), but
        // clamp to whatever's available so a request for the Nth card still
        // returns *something* when fewer than N+1 visible cards exist.
        const index = Math.min(activeGuide.skipIndex, visible.length - 1);
        attach(visible[index]);
        return;
      }
      if (Date.now() - start < TARGET_MAX_WAIT_MS) {
        retryTimer = setTimeout(tryFind, TARGET_RETRY_INTERVAL_MS);
      } else {
        endGuide();
      }
    };

    tryFind();

    const onResizeOrScroll = () => {
      update();
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    };
    window.addEventListener("resize", onResizeOrScroll);
    window.addEventListener("scroll", onResizeOrScroll, true);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      resizeObserver?.disconnect();
      if (target) target.removeEventListener("mousedown", onTargetClick);
      window.removeEventListener("resize", onResizeOrScroll);
      window.removeEventListener("scroll", onResizeOrScroll, true);
    };
  }, [activeGuide, endGuide]);

  if (!activeGuide || !rect) return null;

  const cutout = {
    x: rect.left - PADDING,
    y: rect.top - PADDING,
    width: rect.width + PADDING * 2,
    height: rect.height + PADDING * 2,
  };
  // CSS border-radius scales proportionally when it exceeds half the smaller
  // dimension (keeps circular corners); SVG rx/ry clamp independently and end
  // up elliptical. Match CSS by clamping both to the smaller half-dimension.
  const effectiveRadius = Math.min(
    radius,
    cutout.width / 2,
    cutout.height / 2
  );

  return createPortal(
    <>
      {activeGuide.mode === "overlay" && (
        <OverlaySvg>
          <path
            d={buildOverlayPath(cutout, effectiveRadius, viewport.w, viewport.h)}
            fill="rgba(0, 0, 0, 0.5)"
            fillRule="evenodd"
            onClick={endGuide}
            style={{ pointerEvents: "visiblePainted", cursor: "pointer" }}
          />
        </OverlaySvg>
      )}
      <Outline
        style={{
          top: cutout.y,
          left: cutout.x,
          width: cutout.width,
          height: cutout.height,
          borderRadius: effectiveRadius,
        }}
      />
    </>,
    document.body
  );
};

export default GuideHighlight;
